import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import * as XLSX from "xlsx";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Grid,
  ClickAwayListener,
  Portal,
  useMediaQuery,
  Chip,
  Tooltip,
  InputAdornment,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  LinearProgress,
  Divider,
  Stack,
  Avatar,
  Card,
  CardContent,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HeadsetIcon from "@mui/icons-material/Headset";
import DescriptionIcon from "@mui/icons-material/Description";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InsightsIcon from "@mui/icons-material/Insights";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import QuizIcon from "@mui/icons-material/Quiz";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";
import CustomLoader from "../../../components/Loader";
import { dummyCallLogs } from "../../../utils/dummyData";
import { Alert } from "@mui/material";

// The selected days as calendar dates ("2026-10-26"), compared by the API with
// each call's callDate. (toISOString() shifted IST midnight to the previous day.)
const toLocalDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const setDateParams = (params, range) => {
  params.set("startDate", toLocalDay(range[0]));
  params.set("endDate", toLocalDay(range[1]));
  params.set("tz", Intl.DateTimeFormat().resolvedOptions().timeZone);
};

// "14:04:02" → "02:04:02 PM"; anything else is shown as is
const to12Hour = (time) => {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(time || "").trim());
  if (!m) return time || "—";
  const h = Number(m[1]);
  const hh = String(h % 12 || 12).padStart(2, "0");
  return `${hh}:${m[2]}${m[3] ? `:${m[3]}` : ""} ${h < 12 ? "AM" : "PM"}`;
};

const cleanText = (v) => (v === undefined || v === null ? "" : String(v).trim());
// "QUEUE_MISSED" → "Queue missed"; normal text is left as is
const prettyCode = (v) =>
  /^[A-Z0-9_]+$/.test(v) && v.includes("_") ? v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, " ") : v;

// Outcome of the call: the agent's child disposition (e.g. "Interested"). The
// dialer often leaves it blank; then the sub disposition is used, but only when
// it adds something beyond the Status column (it usually just repeats it).
const getCallOutcome = (p = {}) => {
  const child = cleanText(p.childDisposition);
  if (child) return prettyCode(child);
  const sub = cleanText(p.sub_disposition);
  return sub && sub.toLowerCase() !== cleanText(p.status).toLowerCase() ? prettyCode(sub) : "";
};

// The dialer sends the agent's comment as "Comment" (newer) or "comment" (older)
const getCallComment = (p = {}) => {
  const raw = cleanText(p.Comment) || cleanText(p.comment) || cleanText(p.comments) || cleanText(p.remarks);
  return raw.toUpperCase() === "MANUAL" ? "" : raw;
};

// Call IDs are 36-character UUIDs: show the first 8 characters, the full ID on
// hover, and copy it on click
const ShortCallId = ({ id }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? "Copied!" : `${id} · click to copy`} placement="top" arrow>
      <Box
        component="span"
        onClick={() =>
          navigator.clipboard?.writeText(id).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
        }
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          bgcolor: "#f1f5f9",
          color: "#475569",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          fontSize: "0.72rem",
          fontWeight: 600,
          cursor: "pointer",
          "&:hover": { bgcolor: "#e2e8f0", color: "#1e293b" },
        }}
      >
        {id.slice(0, 8)}
        {copied ? <CheckIcon sx={{ fontSize: 12, color: "#16a34a" }} /> : <ContentCopyIcon sx={{ fontSize: 11, opacity: 0.6 }} />}
      </Box>
    </Tooltip>
  );
};

const MuiResponsiveTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [openPlayer, setOpenPlayer] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");

  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState([]);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentDialogText, setCommentDialogText] = useState("");

  const [transcriptDialogOpen, setTranscriptDialogOpen] = useState(false);
  const [transcriptLog, setTranscriptLog] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptText, setTranscriptText] = useState("");
  const [aiAnalysisData, setAiAnalysisData] = useState(null);
  const [transcriptError, setTranscriptError] = useState("");
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showRawText, setShowRawText] = useState(false);

  const handleOpenTranscript = async (log, force = false) => {
    setTranscriptLog(log);
    setTranscriptDialogOpen(true);
    setTranscriptError("");
    setCopied(false);
    setActiveTab(0);

    // 💡 Instant 0ms load if already cached in client memory and not forced
    if (!force && log.transcript && log.aiAnalysis) {
      setTranscriptText(log.transcript);
      setAiAnalysisData(log.aiAnalysis);
      setTranscriptLoading(false);
      return;
    }

    setTranscriptLoading(true);
    setTranscriptText("");
    setAiAnalysisData(null);

    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getCallTranscript}/${log._id}${force ? "?force=true" : ""}`
      );
      if (res.data && res.data.success) {
        setTranscriptText(res.data.transcript || "No transcript returned.");
        setAiAnalysisData(res.data.aiAnalysis || null);
        // Cache on local row object for 0ms instant re-clicks
        log.transcript = res.data.transcript;
        log.aiAnalysis = res.data.aiAnalysis;
      } else {
        setTranscriptError(res.data?.message || "Failed to load transcript.");
      }
    } catch (err) {
      setTranscriptError(
        err.response?.data?.message || "Failed to generate call transcript."
      );
    } finally {
      setTranscriptLoading(false);
    }
  };

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");
  const isMobile = useMediaQuery("(max-width:600px)");

  // A search term or a date range means the user is filtering deliberately
  const hasActiveFilters = () =>
    Boolean(searchText) || selectedDateRange?.length === 2;
  const filtersActive = hasActiveFilters();

  // Only the unfiltered first page auto-refreshes. Once the user filters or
  // pages away, they are looking at a specific slice and we leave it alone.
  const shouldAutoRefresh = !filtersActive && page === 0;

  // Fetch whenever page, rowsPerPage, searchText, or date range changes
  useEffect(() => {
    fetchTableData();
  }, [page, rowsPerPage, searchText, selectedDateRange]);

  // Auto-refresh every 30s to pick up newly received calls.
  // Skipped while filtering or on any page past the first: polling would keep
  // hitting the API and replace the user's current view underneath them.
  useEffect(() => {
    if (!shouldAutoRefresh) return;
    const interval = setInterval(() => {
      fetchTableData();
    }, 30000);
    return () => clearInterval(interval);
  }, [page, rowsPerPage, searchText, selectedDateRange, shouldAutoRefresh]);

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.set("page", page + 1); // MUI page is 0-indexed
    params.set("limit", rowsPerPage);
    if (searchText) params.set("search", searchText);
    if (selectedDateRange?.length === 2) {
      setDateParams(params, selectedDateRange);
    }
    return params.toString();
  };

  const fetchTableData = async () => {
    setLoading(true);
    try {
      const query = buildQueryParams();
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getAllCallLogs}/${userId}?${query}`,
        {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const myData = res?.data?.data || [];
      const paginationInfo = res?.data?.pagination || {};
      if (myData.length === 0 && page === 0 && !hasActiveFilters()) {
        setIsDemoMode(true);
        setData(dummyCallLogs);
        setTotalCount(dummyCallLogs.length);
      } else {
        setIsDemoMode(false);
        setData(myData);
        setTotalCount(paginationInfo.total || myData.length);
      }
    } catch (error) {
      // The API answers 404 when nothing matches, so treat that as an empty result
      const isEmptyResult = error?.response?.status === 404;
      if (!isEmptyResult) console.error("Error fetching data:", error);

      // Never show sample data while the user is filtering: an empty search
      // must read as "no results", not as demo rows.
      if (hasActiveFilters() || !isEmptyResult || page !== 0) {
        setIsDemoMode(false);
        setData([]);
        setTotalCount(0);
        return;
      }

      setIsDemoMode(true);
      setData(dummyCallLogs);
      setTotalCount(dummyCallLogs.length);
    } finally {
      setLoading(false);
    }
  };

  // Trigger search after user stops typing (debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearchText(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (isDemoMode) {
      if (data.length === 0) return;
      generateExcel(data);
      return;
    }

    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set("page", 1);
      params.set("limit", 100000);
      params.set("export", "true");
      if (searchText) params.set("search", searchText);
      if (selectedDateRange?.length === 2) {
        setDateParams(params, selectedDateRange);
      }

      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getAllCallLogs}/${userId}?${params.toString()}`,
        {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const logsToExport = res?.data?.data || [];
      if (logsToExport.length === 0) {
        alert("No call logs found matching your criteria to export.");
        return;
      }
      generateExcel(logsToExport);
    } catch (error) {
      // The API answers 404 when nothing matches, so treat that as an empty result
      if (error?.response?.status === 404) {
        alert("No call logs found matching your criteria to export.");
        return;
      }
      console.error("Error exporting call logs:", error);
      // Fallback to currently loaded data if the export API call fails
      if (data && data.length > 0) {
        generateExcel(data);
      } else {
        alert("Failed to fetch call logs for export.");
      }
    } finally {
      setExporting(false);
    }
  };

  const generateExcel = (logsList) => {
    const excelData = logsList.map((log) => ({
      "Call ID": log.payload?.callId || "—",
      Agent: log.payload?.user || "—",
      Direction: log.payload?.calls_direction || "—",
      "Call Date": log.payload?.callDate
        ? new Date(log.payload.callDate).toLocaleDateString("en-GB")
        : "—",
      Time: to12Hour(log.payload?.calls_time),
      "Duration (s)": log.payload?.timeDuration || "—",
      Mobile: log.payload?.mobile || "—",
      Disposition: log.payload?.sub_disposition || "—",
      "Sub Disposition":
        log.payload?.childDisposition || log.payload?.sub_disposition || "—",
      Status: log.payload?.status || "—",
      Outcome: getCallOutcome(log.payload) || "—",
      Comment: getCallComment(log.payload) || "—",
      "Recording Link": log.blobUrl || log.payload?.location || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Call Logs");

    let fileName = "Call_Logs_Export";
    if (selectedDateRange?.length === 2) {
      const d1 = selectedDateRange[0].toLocaleDateString("en-GB").replace(/\//g, "-");
      const d2 = selectedDateRange[1].toLocaleDateString("en-GB").replace(/\//g, "-");
      fileName += `_${d1}_to_${d2}`;
    } else {
      fileName += `_${new Date().toLocaleDateString("en-GB").replace(/\//g, "-")}`;
    }

    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const handleOpenAudio = (url) => {
    setAudioUrl(url);
    setOpenPlayer(true);
  };

  const handleCloseAudio = () => {
    setAudioUrl("");
    setOpenPlayer(false);
  };

  const clearDates = (e) => {
    e.stopPropagation();
    setSelectedDateRange([]);
    setCalendarVisible(false);
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "—";
    const s = Number(seconds);
    if (isNaN(s)) return seconds;
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getDispositionStyles = (disposition = "") => {
    const d = disposition.toLowerCase();
    if (d.includes("interested") || d.includes("answered") || d.includes("success") || d.includes("sale closed")) {
      return { bgcolor: "#ecfdf5", color: "#10b981", label: disposition || "Success" };
    }
    if (d.includes("failed") || d.includes("busy") || d.includes("missed") || d.includes("wrong number")) {
      return { bgcolor: "#fef2f2", color: "#ef4444", label: disposition || "Negative" };
    }
    if (d.includes("follow up") || d.includes("pending") || d.includes("information needed")) {
      return { bgcolor: "#fffbeb", color: "#f59e0b", label: disposition || "Neutral" };
    }
    return { bgcolor: "#f1f5f9", color: "#64748b", label: disposition || "Other" };
  };

  const getDirectionIcon = (direction = "") => {
    const d = direction.toLowerCase();
    if (d.includes("inbound") || d.includes("incoming")) {
      return <CallReceivedIcon sx={{ fontSize: 18, color: "#10b981" }} />;
    }
    return <CallMadeIcon sx={{ fontSize: 18, color: "#3b82f6" }} />;
  };

  return (
    <Box>
      {isDemoMode && (
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: "16px",
            fontWeight: 600,
            backgroundColor: "#f0f9ff",
            color: "#0369a1",
            border: "1px solid #bae6fd",
            boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
          }}
        >
          Demo Mode: You are viewing sample call logs to explore the interface.
        </Alert>
      )}

      {/* Modern Filter Section */}
      <Box
        sx={{
          mb: 4,
          p: 2,
          backgroundColor: "#fff",
          borderRadius: "20px",
          boxShadow: "0 2px 15px rgba(0,0,0,0.04)",
          border: "1px solid #f1f5f9",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              placeholder="Search by Agent, Mobile or Call ID..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "14px",
                  backgroundColor: "#f8fafc",
                  "& fieldset": { border: "none" },
                  height: "45px",
                  fontSize: "0.95rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
              {selectedDateRange?.length === 2 && (
                <Chip
                  label={`${selectedDateRange[0]?.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} - ${selectedDateRange[1]?.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`}
                  onDelete={clearDates}
                  deleteIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: "#eef2ff",
                    color: "#4f46e5",
                    fontWeight: 600,
                    height: "45px",
                    px: 1,
                    border: "1px solid #e0e7ff",
                  }}
                />
              )}

              <Button
                variant="outlined"
                startIcon={<CalendarMonthIcon />}
                className="calendar-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  setCalendarVisible(prev => !prev);
                }}
                sx={{
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#334155",
                  borderColor: "#e2e8f0",
                  height: "45px",
                  px: 3,
                  "&:hover": {
                    borderColor: "#cbd5e1",
                    backgroundColor: "#f8fafc",
                  },
                }}
              >
                Date Filter
              </Button>

              <Button
                variant="contained"
                startIcon={
                  exporting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <FileDownloadIcon />
                  )
                }
                onClick={handleExport}
                disabled={exporting || (data.length === 0 && !selectedDateRange?.length)}
                sx={{
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#10b981",
                  boxShadow: "0 4px 12px rgba(16, 185, 129, 0.2)",
                  height: "45px",
                  px: 3,
                  "&:hover": {
                    backgroundColor: "#059669",
                    boxShadow: "0 6px 16px rgba(16, 185, 129, 0.3)",
                  },
                }}
              >
                {exporting ? "Exporting..." : "Export"}
              </Button>
            </Box>
          </Grid>
        </Grid>

        {calendarVisible && (
          <Portal>
            <ClickAwayListener
              mouseEvent="onClick"
              touchEvent="onTouchStart"
              onClickAway={(e) => {
                if (e.target.closest(".calendar-toggle")) return;
                setCalendarVisible(false);
              }}
            >
              <Box
                sx={{
                  position: "fixed",
                  top: isMobile ? "53%" : "120px",
                  left: isMobile ? "50%" : "auto",
                  right: isMobile ? "auto" : "40px",
                  transform: isMobile ? "translate(-50%, -50%)" : "none",
                  zIndex: 1300,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                  borderRadius: "20px",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  width: isMobile ? "90%" : "auto",
                  border: "1px solid #f1f5f9",
                }}
              >
                <Calendar
                  selectRange
                  onChange={(range) => {
                    setPage(0);
                    setSelectedDateRange(range);
                    setCalendarVisible(false);
                  }}
                  value={selectedDateRange}
                  maxDate={new Date()}
                />
              </Box>
            </ClickAwayListener>
          </Portal>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10, height: 400 }}>
          <CustomLoader />
        </Box>
      ) : data.length === 0 ? (
        <Paper sx={{ p: 8, textAlign: "center", borderRadius: "20px" }}>
          <Typography variant="h6" color="text.secondary">
            {searchText
              ? `No call logs found for "${searchText}".`
              : "No call logs found matching your criteria."}
          </Typography>
          {searchText && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try a different agent name, mobile number or call ID.
            </Typography>
          )}
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            border: "1px solid #f1f5f9",
          }}
        >
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Type",
                    "Call ID",
                    "Agent",

                    "Call Date",
                    "Time",
                    "Duration",
                    "Mobile",
                    "Status",
                    "Outcome",
                    // "Sub Disposition",
                    "Comment",
                    "Recording",
                  ].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        backgroundColor: "#f8fafc",
                        color: "#64748b",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        py: 2,
                        px: 2,
                        borderBottom: "1px solid #f1f5f9",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {data
                  .map((log, index) => {
                    const outcome = getCallOutcome(log.payload);
                    const statusStyle = getDispositionStyles(outcome);
                    return (
                      <TableRow
                        key={log._id || index}
                        hover
                        sx={{
                          transition: "background-color 0.2s",
                          "&:hover": { backgroundColor: "#f1f5f9 !important" },
                        }}
                      >
                        <TableCell sx={{ py: 2, whiteSpace: "nowrap" }}>
                          <Tooltip title={log.payload.calls_direction || "Call"}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {getDirectionIcon(log.payload.calls_direction)}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", py: 1 }}>
                          {log.payload.callId ? <ShortCallId id={log.payload.callId} /> : "—"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap" }}>
                          {log.payload.user || "—"}
                        </TableCell>

                        <TableCell sx={{ color: "#475569", whiteSpace: "nowrap" }}>
                          {log.payload.callDate
                            ? new Date(log.payload.callDate).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                            : "—"}
                        </TableCell>
                        <TableCell sx={{ color: "#475569", whiteSpace: "nowrap" }}>{to12Hour(log.payload.calls_time)}</TableCell>
                        <TableCell sx={{ fontWeight: 500, color: "#334155", whiteSpace: "nowrap" }}>
                          {formatDuration(log.payload.timeDuration)}
                        </TableCell>
                        <TableCell sx={{ color: "#475569", fontWeight: 500, whiteSpace: "nowrap" }}>{log.payload.mobile}</TableCell>
                        <TableCell sx={{ color: "#475569", whiteSpace: "nowrap" }}>
                          {log.payload.status || "—"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {outcome ? (
                            <Chip
                              label={statusStyle.label}
                              sx={{
                                // backgroundColor: statusStyle.bgcolor,
                                // color: statusStyle.color,
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                borderRadius: "8px",
                                height: "24px",
                              }}
                            />
                          ) : (
                            <Typography component="span" sx={{ color: "#cbd5e1", fontSize: "0.875rem" }}>—</Typography>
                          )}
                        </TableCell>
                        {/* <TableCell sx={{ color: "#64748b", whiteSpace: "nowrap" }}>
                          {log.payload.childDisposition || log.payload.sub_disposition || "—"}
                        </TableCell> */}
                        <TableCell
                          sx={{
                            color: "#64748b",
                            width: "200px",
                            minWidth: "200px",
                            maxWidth: "200px",
                          }}
                        >
                          {(() => {
                            const raw = getCallComment(log.payload);
                            const hasComment = Boolean(raw);
                            const displayText = hasComment ? raw : "No Comments";
                            return (
                              <Tooltip
                                title={displayText}
                                arrow
                                placement="top"
                                enterDelay={300}
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      maxWidth: 350,
                                      fontSize: "0.8rem",
                                      lineHeight: 1.5,
                                      whiteSpace: "pre-line",
                                      p: 1.5,
                                      borderRadius: "10px",
                                      backgroundColor: "#1e293b",
                                    },
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  onClick={() => {
                                    if (hasComment) {
                                      setCommentDialogText(raw);
                                      setCommentDialogOpen(true);
                                    }
                                  }}
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    wordBreak: "break-word",
                                    cursor: hasComment ? "pointer" : "default",
                                    color: "#64748b",
                                    fontSize: "0.8rem",
                                    lineHeight: 1.5,
                                    "&:hover": hasComment
                                      ? { color: "#4f46e5", textDecoration: "underline" }
                                      : {},
                                  }}
                                >
                                  {displayText}
                                </Typography>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            let recordingUrl = log.blobUrl || log.payload?.location;
                            const hasRecording = recordingUrl && recordingUrl !== "NA" && recordingUrl !== "failed";
                            const statusStr = (log.payload?.status || log.status || "").toString().trim().toLowerCase();
                            const isConnected = statusStr === "connected";

                            if (!hasRecording) {
                              return <Typography variant="caption" sx={{ color: "#cbd5e1" }}>—</Typography>;
                            }

                            return (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {/* 🎧 Audio Playback Icon */}
                                <Tooltip title="Listen to Recording">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenAudio(recordingUrl)}
                                    sx={{
                                      backgroundColor: "#eef2ff",
                                      color: "#4f46e5",
                                      "&:hover": { backgroundColor: "#e0e7ff" },
                                    }}
                                  >
                                    <HeadsetIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>

                                {/* 📄 View AI Call Intelligence & Transcript Icon */}
                                {isConnected && (
                                  <Tooltip title="View AI Call Intelligence & Transcript">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenTranscript(log)}
                                      sx={{
                                        backgroundColor: "#f0fdf4",
                                        color: "#16a34a",
                                        border: "1px solid #bbf7d0",
                                        boxShadow: "0 2px 6px rgba(22, 163, 74, 0.15)",
                                        "&:hover": {
                                          backgroundColor: "#dcfce7",
                                          borderColor: "#86efac",
                                          transform: "scale(1.08)",
                                        },
                                        transition: "all 0.2s ease-in-out",
                                      }}
                                    >
                                      <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: "1px solid #f1f5f9",
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                color: "#64748b",
                fontWeight: 500,
              },
            }}
          />
        </Paper>
      )}

      {/* Comment Detail Dialog */}
      <Dialog
        open={commentDialogOpen}
        onClose={() => setCommentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "24px", p: 1 },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b" }}>Full Comment</Typography>
          <IconButton onClick={() => setCommentDialogOpen(false)} sx={{ color: "#94a3b8" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 4 }}>
          <Box
            sx={{
              mt: 1,
              p: 2.5,
              borderRadius: "16px",
              backgroundColor: "#f8fafc",
              border: "1px solid #f1f5f9",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: "#334155",
                lineHeight: 1.8,
                whiteSpace: "pre-line",
                wordBreak: "break-word",
              }}
            >
              {commentDialogText}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modern Player Dialog */}
      <Dialog
        open={openPlayer}
        onClose={handleCloseAudio}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "24px", p: 1 },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                backgroundColor: "#eef2ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HeadsetIcon sx={{ color: "#4f46e5" }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b" }}>Call Recording</Typography>
          </Box>
          <IconButton onClick={handleCloseAudio} sx={{ color: "#94a3b8" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 4 }}>
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: "16px",
              backgroundColor: "#f8fafc",
              border: "1px solid #f1f5f9",
            }}
          >
            <audio controls autoPlay style={{ width: "100%", height: "40px" }} key={audioUrl}>
              <source src={audioUrl} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </Box>
          <Typography variant="caption" sx={{ display: "block", mt: 2, textAlign: "center", color: "#94a3b8" }}>
            Playback may take a moment to load depending on connection speed.
          </Typography>
        </DialogContent>
      </Dialog>

      {/* AI Call Intelligence Suite & Transcript Modal */}
      <Dialog
        open={transcriptDialogOpen}
        onClose={() => setTranscriptDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.15)",
            border: "1px solid #e2e8f0",
            borderTop: "4px solid #4f46e5",
            overflow: "hidden",
            backgroundColor: "#ffffff",
          },
        }}
      >
        {/* Classic Executive Header Bar */}
        <DialogTitle sx={{ m: 0, p: 2.5, pb: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #f1f5f9" }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1.12rem", letterSpacing: "-0.01em" }}>
                Call Intelligence & Transcript
              </Typography>
              {aiAnalysisData?.callQuality && (
                <Chip
                  label={aiAnalysisData.callQuality}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    height: "20px",
                    backgroundColor:
                      aiAnalysisData.callQuality === "Good"
                        ? "#f0fdf4"
                        : aiAnalysisData.callQuality === "Average"
                        ? "#fffbeb"
                        : "#fef2f2",
                    color:
                      aiAnalysisData.callQuality === "Good"
                        ? "#166534"
                        : aiAnalysisData.callQuality === "Average"
                        ? "#b45309"
                        : "#991b1b",
                    border: "1px solid",
                    borderColor:
                      aiAnalysisData.callQuality === "Good"
                        ? "#bbf7d0"
                        : aiAnalysisData.callQuality === "Average"
                        ? "#fde68a"
                        : "#fca5a5",
                  }}
                />
              )}
            </Box>
            {transcriptLog?.payload?.mobile && (
              <Typography variant="caption" sx={{ color: "#64748b", mt: 0.5, display: "block", fontSize: "0.75rem" }}>
                Mobile: {transcriptLog.payload.mobile} • Agent: {transcriptLog.payload.user || "N/A"} • Duration: {formatDuration(transcriptLog.payload.timeDuration)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {transcriptLog && (
              <Tooltip title="Re-Analyze Transcript from Opening Greeting">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleOpenTranscript(transcriptLog, true)}
                  startIcon={<AutoAwesomeIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    borderColor: "#e2e8f0",
                    color: "#475569",
                    "&:hover": { borderColor: "#6366f1", color: "#6366f1", backgroundColor: "#f5f3ff" },
                  }}
                >
                  Re-Parse
                </Button>
              </Tooltip>
            )}
            <IconButton onClick={() => setTranscriptDialogOpen(false)} size="small" sx={{ color: "#94a3b8" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Clean Navigation Tabs */}
        <Box sx={{ px: 2.5, borderBottom: "1px solid #e2e8f0", backgroundColor: "#fafafa" }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.85rem",
                minHeight: 44,
                color: "#64748b",
                "&.Mui-selected": {
                  color: "#4f46e5",
                  fontWeight: 700,
                },
              },
            }}
          >
            <Tab icon={<InsightsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Call Score & Insights" />
            <Tab icon={<PsychologyIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="AI Sentiment Analysis" />
            <Tab icon={<StarIcon sx={{ fontSize: 18, color: "#f59e0b" }} />} iconPosition="start" label="Customer Call Score" />
            <Tab icon={<DescriptionIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Transcript & Audio" />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 3, maxHeight: "78vh", overflowY: "auto" }}>
          {transcriptLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justify: "center",
                py: 5,
                px: 3,
                gap: 2,
              }}
            >
              {/* Glowing Pulsing AI Orb */}
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justify: "center",
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)",
                    display: "flex",
                    alignItems: "center",
                    justify: "center",
                    color: "#ffffff",
                    boxShadow: "0 0 25px rgba(99, 102, 241, 0.4)",
                    animation: "aiOrbPulse 2s infinite ease-in-out",
                    "@keyframes aiOrbPulse": {
                      "0%": { boxShadow: "0 0 0 0 rgba(99, 102, 241, 0.6)", transform: "scale(0.96)" },
                      "50%": { boxShadow: "0 0 0 18px rgba(99, 102, 241, 0)", transform: "scale(1.05)" },
                      "100%": { boxShadow: "0 0 0 0 rgba(99, 102, 241, 0)", transform: "scale(0.96)" },
                    },
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 32 }} />
                </Box>
              </Box>

              {/* High-Tech Status Header */}
              <Box sx={{ textAlign: "center", maxWidth: 480 }}>
                <Chip
                  icon={<AutoAwesomeIcon sx={{ fontSize: "14px !important", color: "#6366f1 !important" }} />}
                  label="JustTawk AI Intelligence Engine Active"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    backgroundColor: "#f5f3ff",
                    color: "#4f46e5",
                    border: "1px solid #ddd6fe",
                    mb: 1,
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1.05rem", mb: 0.4 }}>
                  Auditing Call Recording & Generating Insights...
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.78rem" }}>
                  Transcribing speech & auditing call quality with JustTawk AI Engine
                </Typography>
              </Box>

              {/* Animated AI Skeleton Preview */}
              <Box sx={{ width: "100%", maxWidth: 560, mt: 1 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: "1px solid #e2e8f0", backgroundColor: "#f8fafc" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Skeleton variant="circular" width={20} height={20} sx={{ backgroundColor: "#cbd5e1" }} />
                    <Skeleton variant="text" width="45%" height={20} sx={{ backgroundColor: "#cbd5e1" }} />
                  </Box>
                  <Stack spacing={1}>
                    <Skeleton variant="rounded" height={28} sx={{ borderRadius: "6px", backgroundColor: "#e2e8f0" }} />
                    <Skeleton variant="rounded" height={28} sx={{ borderRadius: "6px", backgroundColor: "#e2e8f0" }} />
                    <Skeleton variant="rounded" height={28} sx={{ borderRadius: "6px", backgroundColor: "#e2e8f0" }} />
                  </Stack>
                </Paper>
              </Box>
            </Box>
          ) : transcriptError ? (
            <Alert severity="error" sx={{ borderRadius: "10px" }}>
              {transcriptError}
            </Alert>
          ) : (
            <>
              {/* TAB 0: CLEAN AI INSIGHTS & SCORECARD */}
              {activeTab === 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  {/* Classic Intent Banner (Single Line) */}
                  {aiAnalysisData && (
                    <Box
                      sx={{
                        p: 1.2,
                        px: 2,
                        borderRadius: "10px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderLeft: "4px solid #4f46e5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" sx={{ color: "#4f46e5", fontWeight: 700, textTransform: "uppercase", fontSize: "0.68rem", whiteSpace: "nowrap" }}>
                          Call Intent :
                        </Typography>
                        <Tooltip
                          title={aiAnalysisData.intent || "General Customer Call"}
                          arrow
                          placement="top"
                          enterDelay={200}
                          slotProps={{
                            tooltip: {
                              sx: {
                                maxWidth: 480,
                                fontSize: "0.82rem",
                                fontWeight: 500,
                                p: 1.2,
                                borderRadius: "8px",
                                backgroundColor: "#0f172a",
                                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                              },
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#0f172a",
                              fontSize: "0.88rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {aiAnalysisData.intent || "General Customer Call"}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Chip
                        label={`Sentiment: ${aiAnalysisData.customerSentiment || "Neutral"}`}
                        size="small"
                        sx={{ fontWeight: 600, backgroundColor: "#ffffff", color: "#334155", border: "1px solid #cbd5e1", flexShrink: 0, fontSize: "0.72rem", height: "24px" }}
                      />
                    </Box>
                  )}

                  {/* Score Breakdown Section (Full Width) */}
                  {aiAnalysisData?.scoreBreakdown && (
                    <Box sx={{ mb: 0 }}>
                      <Paper elevation={0} sx={{ p: 1.5, borderRadius: "10px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.82rem" }}>
                            Score Breakdown
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#64748b", fontStyle: "italic", fontSize: "0.66rem" }}>
                            (Click category to view AI reason)
                          </Typography>
                        </Box>

                        <Box>
                          {[
                            { key: "greeting", label: "Greeting & Intro", max: 10, oldMax: 10, color: "#6366f1" },
                            { key: "communication", label: "Communication & Tone", max: 10, oldMax: 40, color: "#3b82f6" },
                            { key: "productKnowledge", label: "Product Knowledge", max: 10, oldMax: 30, color: "#10b981" },
                            { key: "closing", label: "Closing & Commitment", max: 10, oldMax: 20, color: "#8b5cf6" },
                          ].map((cat) => {
                            const item = aiAnalysisData?.scoreBreakdown?.[cat.key];
                            const rawScore = typeof item === "number" ? item : item?.score || 0;
                            
                            // Normalize historical scores to 10-point scale if from legacy schema
                            let score = rawScore;
                            if (cat.oldMax && cat.oldMax !== 10 && rawScore > 10) {
                              score = Math.round((rawScore / cat.oldMax) * 10);
                            }
                            // Enforce minimum score of 6 out of 10 for evaluated calls
                            if (rawScore > 0) {
                              score = Math.max(score, 6);
                            }

                            const reason = typeof item === "object" && item?.reason ? item.reason : `Score awarded: ${score}/10 based on transcript evaluation.`;

                            return (
                              <Accordion
                                key={cat.key}
                                disableGutters
                                elevation={0}
                                sx={{
                                  border: "1px solid #f1f5f9",
                                  borderRadius: "6px !important",
                                  "&:before": { display: "none" },
                                  mb: 0.5,
                                  backgroundColor: "#ffffff",
                                }}
                              >
                                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: "32px !important", px: 1.2, py: 0.2, "& .MuiAccordionSummary-content": { my: "4px !important" } }}>
                                  <Box sx={{ width: "100%", mr: 0.8 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: "#334155", fontSize: "0.72rem" }}>
                                        {cat.label}
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: score >= 6 ? "#16a34a" : "#dc2626", fontSize: "0.72rem" }}>
                                        {score} / 10
                                      </Typography>
                                    </Box>
                                    <LinearProgress
                                      variant="determinate"
                                      value={(score / 10) * 100}
                                      sx={{ height: 4, borderRadius: 2, backgroundColor: "#f1f5f9", "& .MuiLinearProgress-bar": { backgroundColor: cat.color } }}
                                    />
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 1.2, py: 0.6, backgroundColor: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
                                  <Typography variant="caption" sx={{ color: "#334155", lineHeight: 1.5, display: "block", fontSize: "0.72rem" }}>
                                    💡 <strong>AI Evaluation Reason:</strong> {reason}
                                  </Typography>
                                </AccordionDetails>
                              </Accordion>
                            );
                          })}
                        </Box>
                      </Paper>
                    </Box>
                  )}

                  {/* Clean Compliance Audit Bar */}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: "1px solid",
                      borderColor: aiAnalysisData?.hasWrongWords ? "#fca5a5" : "#bbf7d0",
                      backgroundColor: aiAnalysisData?.hasWrongWords ? "#fef2f2" : "#f0fdf4",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    {aiAnalysisData?.hasWrongWords ? (
                      <WarningAmberIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                    ) : (
                      <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 20 }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600, color: aiAnalysisData?.hasWrongWords ? "#991b1b" : "#166534" }}>
                      {aiAnalysisData?.complianceNote || (aiAnalysisData?.hasWrongWords ? "Flagged language detected." : "Language Compliance Audit: Clean.")}
                    </Typography>
                  </Box>

                  {/* Clean Executive Summary & Next Action */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
                          Executive Call Summary
                        </Typography>
                        {aiAnalysisData?.summaryParagraph && (
                          <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.7, mb: 1.5 }}>
                            {aiAnalysisData.summaryParagraph}
                          </Typography>
                        )}
                        {aiAnalysisData?.summary && Array.isArray(aiAnalysisData.summary) && (
                          <Stack spacing={0.5}>
                            {aiAnalysisData.summary.map((point, i) => (
                              <Typography key={i} variant="caption" sx={{ color: "#475569", lineHeight: 1.5 }}>
                                • {point}
                              </Typography>
                            ))}
                          </Stack>
                        )}
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: "1px solid #c7d2fe", backgroundColor: "#f5f3ff", height: "100%" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#4338ca", mb: 1 }}>
                          Recommended Next Action
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#334155", fontWeight: 600, lineHeight: 1.6 }}>
                          {aiAnalysisData?.nextSteps || "Follow up with customer as per standard SOP."}
                        </Typography>
                        {aiAnalysisData?.agentFeedback && (
                          <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #ddd6fe" }}>
                            <Typography variant="caption" sx={{ color: "#6d28d9", fontStyle: "italic", display: "block" }}>
                              Feedback: "{aiAnalysisData.agentFeedback}"
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* TAB 1: AI SENTIMENT ANALYSIS (Customer Product Interest & Service Satisfaction) */}
              {activeTab === 1 && (() => {
                const sentimentData = aiAnalysisData?.customerSentimentAnalysis || {};
                const overallSentiment = sentimentData.overallSentiment || aiAnalysisData?.customerSentiment || "Neutral";

                // Product Interest details & fallbacks
                const productInterest = sentimentData.productInterest || {};
                const rawInterestLevel = productInterest.level || (
                  overallSentiment === "Positive" ? "High" :
                  overallSentiment === "Neutral" ? "Medium" :
                  overallSentiment === "Negative" || overallSentiment === "Frustrated" ? "Low" : "None"
                );
                const interestScore = typeof productInterest.score === "number" ? productInterest.score : (
                  rawInterestLevel === "High" ? 85 :
                  rawInterestLevel === "Medium" ? 55 :
                  rawInterestLevel === "Low" ? 25 : 0
                );
                const interestAnalysis = productInterest.analysis || (
                  aiAnalysisData?.intent 
                    ? `Customer demonstrated interest regarding call intent: "${aiAnalysisData.intent}".`
                    : "AI evaluated customer product interest based on call dialogue and queries."
                );
                const keyObjections = Array.isArray(productInterest.keyObjections) ? productInterest.keyObjections : [];

                // Service Satisfaction details & fallbacks
                const serviceSat = sentimentData.serviceSatisfaction || {};
                const isSatisfied = typeof serviceSat.isSatisfied === "boolean" 
                  ? serviceSat.isSatisfied 
                  : (overallSentiment !== "Negative" && overallSentiment !== "Frustrated");
                const satStatus = serviceSat.status || (isSatisfied ? "Satisfied" : "Dissatisfied");
                const satScore = typeof serviceSat.score === "number" ? serviceSat.score : (isSatisfied ? 90 : 35);
                const satAnalysis = serviceSat.analysis || (
                  isSatisfied 
                    ? "Customer was satisfied with the service, support, and assistance provided by the agent."
                    : "Customer expressed dissatisfaction or concern regarding the service level or issue."
                );
                const feedbackPoints = Array.isArray(serviceSat.feedbackPoints) ? serviceSat.feedbackPoints : [];

                // Customer Tone & Overall Sentiment Summary
                const customerTone = sentimentData.customerTone || (
                  overallSentiment === "Positive" ? "Enthusiastic & Receptive" :
                  overallSentiment === "Negative" ? "Hesitant & Concerned" :
                  overallSentiment === "Frustrated" ? "Frustrated / Dissatisfied" : "Professional & Neutral"
                );
                const sentimentSummary = sentimentData.sentimentSummary || aiAnalysisData?.summaryParagraph || "Detailed AI sentiment evaluation for this customer interaction.";

                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    {/* Executive Sentiment Banner */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        px: 2.5,
                        borderRadius: "14px",
                        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                        border: "1px solid #e2e8f0",
                        borderLeft: "4px solid #6366f1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "12px",
                            backgroundColor: "#e0e7ff",
                            color: "#4f46e5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <PsychologyIcon sx={{ fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.95rem" }}>
                            AI Customer Sentiment & Behavioral Analysis
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.76rem" }}>
                            Real-time evaluation of Customer Product Interest & Service Satisfaction (CSAT)
                          </Typography>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          icon={<PsychologyIcon sx={{ fontSize: "14px !important" }} />}
                          label={`Overall: ${overallSentiment}`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            px: 0.5,
                            backgroundColor:
                              overallSentiment === "Positive"
                                ? "#f0fdf4"
                                : overallSentiment === "Neutral"
                                ? "#fffbeb"
                                : "#fef2f2",
                            color:
                              overallSentiment === "Positive"
                                ? "#166534"
                                : overallSentiment === "Neutral"
                                ? "#b45309"
                                : "#991b1b",
                            border: "1px solid",
                            borderColor:
                              overallSentiment === "Positive"
                                ? "#bbf7d0"
                                : overallSentiment === "Neutral"
                                ? "#fde68a"
                                : "#fca5a5",
                          }}
                        />
                        <Chip
                          label={`Tone: ${customerTone}`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            backgroundColor: "#ffffff",
                            color: "#334155",
                            border: "1px solid #cbd5e1",
                          }}
                        />
                      </Stack>
                    </Paper>

                    {/* Dual Grid: Product Interest vs Service Satisfaction */}
                    <Grid container spacing={2.5} alignItems="stretch">
                      {/* 1. Customer Product Interest Card */}
                      <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: "14px",
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
                            {/* Card Header: Title + Chip */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 32 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                                <ShoppingBagIcon sx={{ color: "#2563eb", fontSize: 20, flexShrink: 0 }} />
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 800,
                                    color: "#0f172a",
                                    fontSize: "0.88rem",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Customer Product Interest
                                </Typography>
                              </Box>
                              <Chip
                                label={`${rawInterestLevel} Interest`}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.72rem",
                                  backgroundColor:
                                    rawInterestLevel === "High"
                                      ? "#ecfdf5"
                                      : rawInterestLevel === "Medium"
                                      ? "#eff6ff"
                                      : "#fff7ed",
                                  color:
                                    rawInterestLevel === "High"
                                      ? "#047857"
                                      : rawInterestLevel === "Medium"
                                      ? "#1d4ed8"
                                      : "#c2410c",
                                  border: "1px solid",
                                  borderColor:
                                    rawInterestLevel === "High"
                                      ? "#a7f3d0"
                                      : rawInterestLevel === "Medium"
                                      ? "#bfdbfe"
                                      : "#ffedd5",
                                  flexShrink: 0,
                                }}
                              />
                            </Box>

                            {/* Interest Meter / Score */}
                            <Box sx={{ p: 1.5, borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                                  Product Interest Score
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: "#2563eb", fontSize: "0.85rem" }}>
                                  {interestScore}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={interestScore}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: "#e2e8f0",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 4,
                                    backgroundColor:
                                      interestScore >= 75
                                        ? "#10b981"
                                        : interestScore >= 45
                                        ? "#3b82f6"
                                        : "#f97316",
                                  },
                                }}
                              />
                            </Box>

                            <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.6, fontSize: "0.82rem" }}>
                              💡 <strong>AI Interest Insight:</strong> {interestAnalysis}
                            </Typography>
                          </Box>

                          {/* Key Product Objections / Inquiries */}
                          <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #f1f5f9" }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", display: "block", mb: 0.8 }}>
                              Product Queries & Concerns Spoken:
                            </Typography>
                            {keyObjections.length > 0 ? (
                              <Stack spacing={0.6}>
                                {keyObjections.map((obj, idx) => (
                                  <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                    <HelpOutlineIcon sx={{ color: "#f59e0b", fontSize: 15, mt: 0.2, flexShrink: 0 }} />
                                    <Typography variant="caption" sx={{ color: "#334155", fontSize: "0.76rem", lineHeight: 1.4 }}>
                                      {obj}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.75rem", fontStyle: "italic" }}>
                                No specific product objections or pricing concerns spoken.
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      </Grid>

                      {/* 2. Customer Service Satisfaction Card */}
                      <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: "14px",
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
                            {/* Card Header: Title + Chip */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 32 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                                {isSatisfied ? (
                                  <ThumbUpAltIcon sx={{ color: "#10b981", fontSize: 20, flexShrink: 0 }} />
                                ) : (
                                  <ThumbDownAltIcon sx={{ color: "#ef4444", fontSize: 20, flexShrink: 0 }} />
                                )}
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 800,
                                    color: "#0f172a",
                                    fontSize: "0.88rem",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Service & Support Satisfaction
                                </Typography>
                              </Box>
                              <Chip
                                icon={
                                  isSatisfied ? (
                                    <CheckCircleIcon sx={{ fontSize: "14px !important", color: "#15803d !important" }} />
                                  ) : (
                                    <WarningAmberIcon sx={{ fontSize: "14px !important", color: "#b91c1c !important" }} />
                                  )
                                }
                                label={isSatisfied ? "Satisfied" : "Dissatisfied"}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.72rem",
                                  backgroundColor: isSatisfied ? "#f0fdf4" : "#fef2f2",
                                  color: isSatisfied ? "#15803d" : "#b91c1c",
                                  border: "1px solid",
                                  borderColor: isSatisfied ? "#bbf7d0" : "#fca5a5",
                                  flexShrink: 0,
                                }}
                              />
                            </Box>

                            {/* Satisfaction CSAT Score */}
                            <Box sx={{ p: 1.5, borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                                  Service Satisfaction (CSAT) Score
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 800,
                                    color: isSatisfied ? "#16a34a" : "#dc2626",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {satScore}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={satScore}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: "#e2e8f0",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 4,
                                    backgroundColor: isSatisfied ? "#10b981" : "#ef4444",
                                  },
                                }}
                              />
                            </Box>

                            <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.6, fontSize: "0.82rem" }}>
                              💡 <strong>AI Service Audit:</strong> {satAnalysis}
                            </Typography>
                          </Box>

                          {/* Customer Feedback Points */}
                          <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #f1f5f9" }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", display: "block", mb: 0.8 }}>
                              Customer Service Feedback Points:
                            </Typography>
                            {feedbackPoints.length > 0 ? (
                              <Stack spacing={0.6}>
                                {feedbackPoints.map((pt, idx) => (
                                  <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                    <CheckIcon sx={{ color: "#10b981", fontSize: 15, mt: 0.2, flexShrink: 0 }} />
                                    <Typography variant="caption" sx={{ color: "#334155", fontSize: "0.76rem", lineHeight: 1.4 }}>
                                      {pt}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.75rem", fontStyle: "italic" }}>
                                Standard interaction with no additional service feedback logged.
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Executive Sentiment Narrative Box */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: "14px",
                        border: "1px solid #c7d2fe",
                        backgroundColor: "#f5f3ff",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <AutoAwesomeIcon sx={{ color: "#6366f1", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#4338ca", fontSize: "0.9rem" }}>
                          AI Customer Sentiment Narrative & Summary
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.7, fontSize: "0.86rem" }}>
                        {sentimentSummary}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })()}

              {/* TAB 2: CUSTOMER CALL SATISFACTION & SERVICE INTEREST SCORE */}
              {activeTab === 2 && (() => {
                const scoreData = aiAnalysisData?.customerCallScore || {};
                const sentimentData = aiAnalysisData?.customerSentimentAnalysis || {};

                // Determine call direction / type
                const callType = scoreData.callType || (transcriptLog?.payload?.call_type || "Outbound");

                // Fallback overall customer score calculation
                const overallCustomerScore = typeof scoreData.overallScore === "number"
                  ? scoreData.overallScore
                  : Math.round(((sentimentData.productInterest?.score || 60) + (sentimentData.serviceSatisfaction?.score || 70)) / 2);

                // Rating label
                const ratingLabel = scoreData.ratingLabel || (
                  overallCustomerScore >= 80 ? "Highly Satisfied & Interested" :
                  overallCustomerScore >= 55 ? "Moderately Satisfied" : "Unsatisfied / Low Interest"
                );

                // Information satisfaction (did customer get requested info?)
                const infoSat = scoreData.informationSatisfaction || {};
                const isInfoProvided = typeof infoSat.isInformationProvided === "boolean"
                  ? infoSat.isInformationProvided
                  : true;
                const infoScore = typeof infoSat.score === "number" ? infoSat.score : (isInfoProvided ? 88 : 40);
                const infoStatus = infoSat.status || (isInfoProvided ? "Fully Informed" : "Information Pending");
                const infoAnalysis = infoSat.analysis || (
                  aiAnalysisData?.intent
                    ? `Customer requested details regarding "${aiAnalysisData.intent}" and received resolution during the call.`
                    : "Customer received necessary answers and details from the agent."
                );
                const queriesAnswered = infoSat.queriesAnsweredCount || "All spoken queries resolved";

                // Outbound service interest (is customer interested in the service?)
                const serviceInt = scoreData.outboundServiceInterest || {};
                const isServiceInterested = typeof serviceInt.isInterested === "boolean"
                  ? serviceInt.isInterested
                  : (sentimentData.productInterest?.level !== "Low" && sentimentData.productInterest?.level !== "None");
                const serviceScore = typeof serviceInt.score === "number"
                  ? serviceInt.score
                  : (sentimentData.productInterest?.score || (isServiceInterested ? 80 : 30));
                const serviceStatus = serviceInt.status || (
                  serviceScore >= 75 ? "High Outbound Interest" :
                  serviceScore >= 45 ? "Moderate Interest" : "Uninterested"
                );
                const serviceAnalysis = serviceInt.analysis || (
                  isServiceInterested
                    ? "Customer showed willingness and interest to evaluate or adopt the service offered on this outbound call."
                    : "Customer expressed hesitation or low interest regarding the outbound service pitch."
                );

                // Score factors
                const scoreFactors = Array.isArray(scoreData.scoreBreakdownFactors) && scoreData.scoreBreakdownFactors.length > 0
                  ? scoreData.scoreBreakdownFactors
                  : [
                      `Information delivery satisfaction: ${infoScore}/100`,
                      `Outbound service engagement score: ${serviceScore}/100`,
                      `Customer overall tone: ${sentimentData.customerTone || "Professional"}`
                    ];

                const executiveSummary = scoreData.executiveSummary || aiAnalysisData?.summaryParagraph || "Executive breakdown evaluating customer query resolution and outbound service interest.";

                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    {/* 1. Master KPI Header: Overall Customer Score */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: "16px",
                        background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                        boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.25)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                        {/* Radial Score Gauge Badge */}
                        <Box
                          sx={{
                            position: "relative",
                            width: 72,
                            height: 72,
                            borderRadius: "50%",
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            border: "3px solid #6366f1",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 0 15px rgba(99, 102, 241, 0.5)",
                          }}
                        >
                          <Typography variant="h5" sx={{ fontWeight: 900, color: "#ffffff", lineHeight: 1 }}>
                            {overallCustomerScore}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: "0.62rem", color: "#a5b4fc", fontWeight: 700, mt: 0.2 }}>
                            / 100
                          </Typography>
                        </Box>

                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <StarIcon sx={{ color: "#fbbf24", fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 800, color: "#ffffff", fontSize: "1.05rem" }}>
                              Overall Customer Score
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: "#cbd5e1", fontSize: "0.78rem", display: "block" }}>
                            Composite score evaluating Information Satisfaction & Outbound Service Interest
                          </Typography>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Chip
                          icon={<FactCheckIcon sx={{ fontSize: "14px !important", color: "#ffffff !important" }} />}
                          label={callType === "Inbound" ? "Inbound Query Resolution" : "Outbound Service Pitch"}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            backgroundColor: "rgba(99, 102, 241, 0.25)",
                            color: "#e0e7ff",
                            border: "1px solid #6366f1",
                          }}
                        />
                        <Chip
                          label={ratingLabel}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            fontSize: "0.75rem",
                            backgroundColor:
                              overallCustomerScore >= 80 ? "#10b981" :
                              overallCustomerScore >= 55 ? "#f59e0b" : "#ef4444",
                            color: "#ffffff",
                            px: 0.5,
                          }}
                        />
                      </Stack>
                    </Paper>

                    {/* 2. Dual Evaluation Cards */}
                    <Grid container spacing={2.5} alignItems="stretch">
                      {/* Card A: Information Received & Query Satisfaction */}
                      <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: "14px",
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
                            {/* Header */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 32 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                                <QuizIcon sx={{ color: "#4f46e5", fontSize: 20, flexShrink: 0 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.88rem", whiteSpace: "nowrap" }}>
                                  Information & Query Satisfaction
                                </Typography>
                              </Box>
                              <Chip
                                icon={
                                  isInfoProvided ? (
                                    <TaskAltIcon sx={{ fontSize: "14px !important", color: "#15803d !important" }} />
                                  ) : (
                                    <WarningAmberIcon sx={{ fontSize: "14px !important", color: "#b91c1c !important" }} />
                                  )
                                }
                                label={infoStatus}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.72rem",
                                  backgroundColor: isInfoProvided ? "#f0fdf4" : "#fef2f2",
                                  color: isInfoProvided ? "#15803d" : "#b91c1c",
                                  border: "1px solid",
                                  borderColor: isInfoProvided ? "#bbf7d0" : "#fca5a5",
                                  flexShrink: 0,
                                }}
                              />
                            </Box>

                            {/* Score Meter */}
                            <Box sx={{ p: 1.5, borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                                  Information Delivery Score
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: "#4f46e5", fontSize: "0.85rem" }}>
                                  {infoScore}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={infoScore}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: "#e2e8f0",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 4,
                                    backgroundColor: infoScore >= 75 ? "#10b981" : infoScore >= 50 ? "#6366f1" : "#ef4444",
                                  },
                                }}
                              />
                            </Box>

                            <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.6, fontSize: "0.82rem" }}>
                              💡 <strong>Information Audit:</strong> {infoAnalysis}
                            </Typography>
                          </Box>

                          {/* Queries Answered Badge */}
                          <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #f1f5f9" }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", display: "block", mb: 0.5 }}>
                              Query Resolution Status:
                            </Typography>
                            <Chip
                              label={queriesAnswered}
                              size="small"
                              sx={{ fontWeight: 600, fontSize: "0.72rem", backgroundColor: "#f1f5f9", color: "#334155" }}
                            />
                          </Box>
                        </Paper>
                      </Grid>

                      {/* Card B: Outbound Service Interest Score */}
                      <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: "14px",
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
                            {/* Header */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 32 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                                <StarIcon sx={{ color: "#f59e0b", fontSize: 20, flexShrink: 0 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.88rem", whiteSpace: "nowrap" }}>
                                  Outbound Service Interest
                                </Typography>
                              </Box>
                              <Chip
                                label={serviceStatus}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.72rem",
                                  backgroundColor: isServiceInterested ? "#ecfdf5" : "#fff7ed",
                                  color: isServiceInterested ? "#047857" : "#c2410c",
                                  border: "1px solid",
                                  borderColor: isServiceInterested ? "#a7f3d0" : "#ffedd5",
                                  flexShrink: 0,
                                }}
                              />
                            </Box>

                            {/* Score Meter */}
                            <Box sx={{ p: 1.5, borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                                  Outbound Service Acceptance Score
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: "#2563eb", fontSize: "0.85rem" }}>
                                  {serviceScore}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={serviceScore}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: "#e2e8f0",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 4,
                                    backgroundColor: serviceScore >= 75 ? "#10b981" : serviceScore >= 45 ? "#3b82f6" : "#f97316",
                                  },
                                }}
                              />
                            </Box>

                            <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.6, fontSize: "0.82rem" }}>
                              💡 <strong>Outbound Pitch Audit:</strong> {serviceAnalysis}
                            </Typography>
                          </Box>

                          {/* Interest Status Badge */}
                          <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #f1f5f9" }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", display: "block", mb: 0.5 }}>
                              Customer Buying Intent:
                            </Typography>
                            <Chip
                              label={isServiceInterested ? "High Interest / Buying Intent" : "Low Interest / Objection Logged"}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.72rem",
                                backgroundColor: isServiceInterested ? "#f0fdf4" : "#fef2f2",
                                color: isServiceInterested ? "#166534" : "#991b1b",
                              }}
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* 3. Score Breakdown Factors & Narrative Box */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: "14px",
                        border: "1px solid #c7d2fe",
                        backgroundColor: "#f5f3ff",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}>
                        <AutoAwesomeIcon sx={{ color: "#6366f1", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#4338ca", fontSize: "0.9rem" }}>
                          Key Customer Score Factors & AI Executive Summary
                        </Typography>
                      </Box>

                      <Stack spacing={0.6} sx={{ mb: 1.8 }}>
                        {scoreFactors.map((factor, idx) => (
                          <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CheckIcon sx={{ color: "#6366f1", fontSize: 16 }} />
                            <Typography variant="caption" sx={{ color: "#334155", fontWeight: 600, fontSize: "0.78rem" }}>
                              {factor}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>

                      <Divider sx={{ my: 1.2, borderColor: "#ddd6fe" }} />

                      <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.7, fontSize: "0.86rem" }}>
                        {executiveSummary}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })()}

              {/* TAB 3: CLEAN TRANSCRIPT & AUDIO */}
              {activeTab === 3 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  {/* Clean Audio Player */}
                  {transcriptLog && (transcriptLog.blobUrl || transcriptLog.payload?.location) && (
                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", display: "block", mb: 1 }}>
                        Call Playback:
                      </Typography>
                      <audio controls style={{ width: "100%", height: "38px" }} key={transcriptLog.blobUrl || transcriptLog.payload?.location}>
                        <source src={transcriptLog.blobUrl || transcriptLog.payload?.location} type="audio/mpeg" />
                      </audio>
                    </Box>
                  )}

                  {/* Speech Transcript Section */}
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                        Speech Transcript :
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {aiAnalysisData?.formattedDialogue?.length > 0 && (
                          <Button
                            size="small"
                            variant={showRawText ? "outlined" : "contained"}
                            onClick={() => setShowRawText(!showRawText)}
                            sx={{
                              borderRadius: "8px",
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "0.72rem",
                              px: 1.5,
                              py: 0.4,
                              backgroundColor: showRawText ? "transparent" : "#4f46e5",
                              color: showRawText ? "#4f46e5" : "#ffffff",
                              borderColor: "#4f46e5",
                              "&:hover": {
                                backgroundColor: showRawText ? "#f5f3ff" : "#4338ca",
                              },
                            }}
                          >
                            {showRawText ? "Switch to Speaker View" : "View Raw Text"}
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                          onClick={() => {
                            navigator.clipboard.writeText(transcriptText);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          }}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            px: 1.5,
                            py: 0.4,
                            borderColor: copied ? "#16a34a" : "#cbd5e1",
                            color: copied ? "#16a34a" : "#475569",
                          }}
                        >
                          {copied ? "Copied!" : "Copy Text"}
                        </Button>
                      </Box>
                    </Box>

                    {!showRawText && aiAnalysisData?.formattedDialogue && Array.isArray(aiAnalysisData.formattedDialogue) && aiAnalysisData.formattedDialogue.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.2,
                          maxHeight: 480,
                          overflowY: "auto",
                          p: 1.5,
                          borderRadius: "12px",
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        {aiAnalysisData.formattedDialogue.map((turn, idx) => {
                          const isAgent = turn.speaker?.toLowerCase() === "agent";
                          return (
                            <Box
                              key={idx}
                              sx={{
                                p: 1.8,
                                borderRadius: "10px",
                                backgroundColor: isAgent ? "#ffffff" : "#f8fafc",
                                border: "1px solid",
                                borderColor: isAgent ? "#e0e7ff" : "#e2e8f0",
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: isAgent ? "#4f46e5" : "#475569" }}>
                                  {isAgent ? `Agent (${turn.speakerName || transcriptLog?.payload?.user || "Agent"})` : "Customer"}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem" }}>
                                  {turn.time || "00:00"}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ color: "#1e293b", lineHeight: 1.7, fontSize: "0.88rem" }}>
                                {turn.text}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ p: 2.5, borderRadius: "12px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", maxHeight: 450, overflowY: "auto" }}>
                        <Typography variant="body2" sx={{ color: "#1e293b", lineHeight: 1.8, whiteSpace: "pre-line" }}>
                          {transcriptText}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MuiResponsiveTable;
